import { SalesOrder, SelectedProduct } from '../../../../types/sales.types';
import { roundMoney } from '../../../../utils/money';
import {
  calculateAdjustedPrice,
  calculateLineFinalPrice,
  clampUniformDiscountValue,
  getMaxUniformDiscountAmount,
  hasLineDiscount,
  hasLineMarkup,
  type LineAdjustmentType,
} from '../../../../utils/sales/line-pricing.utils';

export type ExchangeDiscountType = LineAdjustmentType;

/** Minimum total for new items (amount paid on outgoing items). */
export const getExchangePaidFloor = (paidForExchangedItems: number): number =>
  roundMoney(Math.max(0, paidForExchangedItems));

export const meetsExchangePaidFloor = (
  newItemsTotal: number,
  paidForExchangedItems: number,
): boolean => newItemsTotal >= getExchangePaidFloor(paidForExchangedItems);

export const violatesExchangePaidFloor = (
  newItemsTotal: number,
  paidForExchangedItems: number,
): boolean =>
  paidForExchangedItems > 0 && !meetsExchangePaidFloor(newItemsTotal, paidForExchangedItems);

export const computeOriginalItemsTotal = (
  order: SalesOrder | null,
  itemsToExchange: number[],
): number => {
  if (!order) return 0;
  return roundMoney(
    itemsToExchange.reduce((total, itemId) => {
      const item = order.items.find((i) => i.id === itemId);
      return total + (item ? Number(item.finalPrice || 0) : 0);
    }, 0),
  );
};

export const computePaidForExchangedItems = (
  order: SalesOrder | null,
  originalItemsTotal: number,
): number => {
  if (!order || originalItemsTotal <= 0) return 0;
  const totalPaid = order.payments?.reduce((sum, p) => sum + Number(p.paymentAmount || 0), 0) || 0;
  const orderTotal = Number(order.totalAmount || 0);

  if (orderTotal <= 0) {
    return roundMoney(Math.min(totalPaid, originalItemsTotal));
  }

  return roundMoney((totalPaid / orderTotal) * originalItemsTotal);
};

/** Catalog total for new items (list price, no floor or discounts). */
export const computeNewItemsCatalogTotal = (products: SelectedProduct[]): number =>
  roundMoney(products.reduce((sum, product) => sum + roundMoney(product.originalPrice), 0));

/** Adjusted total after markups (pre-discount). */
export const computeNewItemsAdjustedTotal = (products: SelectedProduct[]): number =>
  roundMoney(
    products.reduce(
      (sum, product) =>
        sum +
        calculateAdjustedPrice(product.originalPrice, product.markupType, product.markupValue),
      0,
    ),
  );

/** Sum of markup amounts on new items (stored adjusted − catalog). */
export const computeNewItemsMarkupAmount = (products: SelectedProduct[]): number =>
  roundMoney(
    products.reduce((sum, product) => {
      const original = roundMoney(product.originalPrice);
      const adjusted =
        product.adjustedPrice != null && Number.isFinite(Number(product.adjustedPrice))
          ? roundMoney(product.adjustedPrice)
          : calculateAdjustedPrice(product.originalPrice, product.markupType, product.markupValue);
      return sum + roundMoney(Math.max(0, adjusted - original));
    }, 0),
  );

/** Effective total: always via resolved line prices (catalog when unmodified). */
export const computeNewItemsExchangeTotal = (
  products: SelectedProduct[],
  _pricingEnabled: boolean,
  resolveFinalPrice: (product: SelectedProduct) => number,
): number => roundMoney(products.reduce((sum, product) => sum + resolveFinalPrice(product), 0));

export const isExchangeDiscountEnabled = (products: SelectedProduct[]): boolean =>
  products.some((product) => hasLineDiscount(product.discountType, product.discountValue));

export const isExchangeMarkupEnabled = (products: SelectedProduct[]): boolean =>
  products.some((product) => hasLineMarkup(product.markupType, product.markupValue));

export const isExchangeLinePricingModified = (products: SelectedProduct[]): boolean =>
  isExchangeDiscountEnabled(products) || isExchangeMarkupEnabled(products);

/** Minimum final price for one new line item to satisfy the paid floor. */
export const getMinFinalPriceForProduct = (
  productId: number,
  newProducts: SelectedProduct[],
  resolveSiblingFinalPrice: (product: SelectedProduct) => number,
  paidFloor: number,
): number => {
  if (newProducts.length === 0) return getExchangePaidFloor(paidFloor);

  const othersTotal = roundMoney(
    newProducts
      .filter((p) => p.warehouseInventoryProduct.id !== productId)
      .reduce((sum, p) => sum + resolveSiblingFinalPrice(p), 0),
  );

  return roundMoney(Math.max(0, getExchangePaidFloor(paidFloor) - othersTotal));
};

/** Max discount amount vs adjusted price (post-markup). */
export const getMaxDiscountAmountForExchange = (
  adjustedPrice: number,
  minFinalPrice: number,
): number => roundMoney(Math.max(0, adjustedPrice - minFinalPrice));

/** Max discount percentage vs adjusted price (post-markup). */
export const getMaxDiscountPercentageForExchange = (
  adjustedPrice: number,
  minFinalPrice: number,
): number => {
  if (adjustedPrice <= 0) return 0;
  const minPct = (minFinalPrice / adjustedPrice) * 100;
  return roundMoney(Math.max(0, Math.min(99, 100 - minPct)));
};

const resolveAdjustedPrice = (product: SelectedProduct): number =>
  calculateAdjustedPrice(product.originalPrice, product.markupType, product.markupValue);

/** Nominal line price from markup + discount (no exchange credit floor). */
export const resolveNominalExchangeFinalPrice = (product: SelectedProduct): number =>
  calculateLineFinalPrice(
    product.originalPrice,
    product.markupType,
    product.markupValue,
    product.discountType,
    product.discountValue,
  );

const distributeExchangeTotalByWeight = (
  items: Array<{ id: number; weight: number }>,
  targetTotal: number,
): Map<number, number> => {
  const result = new Map<number, number>();
  if (items.length === 0) return result;

  const normalized = items.map((item) => ({
    id: item.id,
    weight: roundMoney(item.weight),
  }));

  const positive = normalized.filter((item) => item.weight > 0);
  normalized
    .filter((item) => item.weight <= 0)
    .forEach((item) => {
      result.set(item.id, 0);
    });

  if (positive.length === 0) {
    return result;
  }

  const weightTotal = roundMoney(positive.reduce((sum, item) => sum + item.weight, 0));
  const safeTarget = roundMoney(Math.max(0, targetTotal));
  let allocated = 0;

  positive.forEach((item, index) => {
    if (index === positive.length - 1) {
      result.set(item.id, roundMoney(Math.max(0, safeTarget - allocated)));
      return;
    }
    const share = roundMoney((safeTarget * item.weight) / weightTotal);
    result.set(item.id, share);
    allocated = roundMoney(allocated + share);
  });

  return result;
};

/**
 * Global resolver: one consistent final price per line honoring exchange credit.
 * Aligns with backend exchange-pricing.util.ts (adjusted weights when redistributing).
 */
export const resolveExchangeLinePrices = (
  products: SelectedProduct[],
  discountEnabled: boolean,
  credit: number,
): Map<number, number> => {
  const result = new Map<number, number>();
  if (products.length === 0) return result;

  if (!isExchangeLinePricingModified(products)) {
    products.forEach((product) => {
      result.set(product.warehouseInventoryProduct.id, roundMoney(product.originalPrice));
    });
    return result;
  }

  const floor = getExchangeCredit(credit);

  const lineItems = products.map((product) => ({
    id: product.warehouseInventoryProduct.id,
    catalog: roundMoney(product.originalPrice),
    adjusted: resolveAdjustedPrice(product),
    nominal: resolveNominalExchangeFinalPrice(product),
  }));

  const nominalTotal = roundMoney(lineItems.reduce((sum, item) => sum + item.nominal, 0));
  const adjustedTotal = roundMoney(lineItems.reduce((sum, item) => sum + item.adjusted, 0));

  if (nominalTotal >= floor && nominalTotal <= adjustedTotal + 0.01) {
    lineItems.forEach((item) => result.set(item.id, item.nominal));
    return result;
  }

  if (nominalTotal < floor) {
    return distributeExchangeTotalByWeight(
      lineItems.map((item) => ({ id: item.id, weight: item.adjusted })),
      floor,
    );
  }

  if (!discountEnabled && nominalTotal > adjustedTotal + 0.01) {
    lineItems.forEach((item) => result.set(item.id, item.adjusted));
    return result;
  }

  return distributeExchangeTotalByWeight(
    lineItems.map((item) => ({ id: item.id, weight: item.adjusted })),
    adjustedTotal,
  );
};

/**
 * Resolves final line price for exchange context.
 */
export const computeExchangeProductFinalPrice = (
  product: SelectedProduct,
  newProducts: SelectedProduct[],
  _discountEnabled: boolean,
  paidFloor: number,
): number => {
  const discountEnabled = isExchangeDiscountEnabled(newProducts);
  const prices = resolveExchangeLinePrices(newProducts, discountEnabled, paidFloor);
  return prices.get(product.warehouseInventoryProduct.id) ?? roundMoney(product.originalPrice);
};

/** Minimum final price for one line when siblings keep their resolved exchange prices. */
export const getExchangeMinFinalPriceForProduct = (
  productId: number,
  products: SelectedProduct[],
  _discountEnabled: boolean,
  credit: number,
): number => {
  if (products.length === 0) {
    return 0;
  }

  if (!isExchangeLinePricingModified(products) && getExchangeCredit(credit) <= 0) {
    const product = products.find((p) => p.warehouseInventoryProduct.id === productId);
    return roundMoney(product?.originalPrice ?? 0);
  }

  const withoutTargetDiscount = products.map((product) => {
    if (product.warehouseInventoryProduct.id !== productId) {
      return product;
    }
    const adjustedPrice = resolveAdjustedPrice(product);
    return {
      ...product,
      discountType: 'NONE' as const,
      discountValue: 0,
      finalPrice: adjustedPrice,
      adjustedPrice,
    };
  });

  const discountEnabled = isExchangeDiscountEnabled(withoutTargetDiscount);
  const resolvedOthers = resolveExchangeLinePrices(withoutTargetDiscount, discountEnabled, credit);
  const othersTotal = roundMoney(
    products
      .filter((product) => product.warehouseInventoryProduct.id !== productId)
      .reduce(
        (sum, product) =>
          sum +
          (resolvedOthers.get(product.warehouseInventoryProduct.id) ??
            resolveAdjustedPrice(product)),
        0,
      ),
  );

  return roundMoney(Math.max(0, getExchangeCredit(credit) - othersTotal));
};

/** Exchange credit from outgoing items (UI: crédito del intercambio). */
export const getExchangeCredit = (paidForExchangedItems: number): number =>
  getExchangePaidFloor(paidForExchangedItems);

export const meetsExchangeCredit = meetsExchangePaidFloor;

export const violatesExchangeCredit = violatesExchangePaidFloor;

/** Max total discount before final total drops below exchange credit. */
export const getExchangeMaxDiscount = (adjustedTotal: number, credit: number): number =>
  roundMoney(Math.max(0, adjustedTotal - getExchangeCredit(credit)));

/** Max uniform percentage before total drops below exchange credit. */
export const getMaxUniformDiscountPercentageForExchange = (
  adjustedTotal: number,
  credit: number,
): number => {
  if (adjustedTotal <= 0) return 0;
  const floor = getExchangeCredit(credit);
  if (floor <= 0) return 99;
  if (floor >= adjustedTotal) return 0;
  return roundMoney(Math.max(0, Math.min(99, 100 - (floor / adjustedTotal) * 100)));
};

/** Max uniform order-level amount before total drops below exchange credit. */
export const getMaxUniformDiscountAmountForExchange = (
  products: SelectedProduct[],
  credit: number,
): number => {
  if (products.length === 0) return 0;

  const adjustedProducts = products.map((product) => ({
    adjustedPrice: resolveAdjustedPrice(product),
  }));
  const adjustedTotal = roundMoney(
    adjustedProducts.reduce((sum, product) => sum + product.adjustedPrice, 0),
  );
  const floor = getExchangeCredit(credit);
  const maxByCredit = roundMoney(Math.max(0, adjustedTotal - floor));
  const maxByMinLine = getMaxUniformDiscountAmount(adjustedProducts);

  return Math.min(maxByCredit, maxByMinLine);
};

export const clampExchangeUniformDiscountValue = (
  discountType: 'PERCENTAGE' | 'AMOUNT',
  value: number,
  products: SelectedProduct[],
  credit: number,
): number => {
  const adjustedProducts = products.map((product) => ({
    adjustedPrice: resolveAdjustedPrice(product),
  }));
  const clamped = clampUniformDiscountValue(discountType, value, adjustedProducts);
  if (products.length === 0) return clamped;

  const adjustedTotal = roundMoney(
    adjustedProducts.reduce((sum, product) => sum + product.adjustedPrice, 0),
  );
  if (credit <= 0 || adjustedTotal <= 0) return clamped;

  if (discountType === 'PERCENTAGE') {
    return Math.min(clamped, getMaxUniformDiscountPercentageForExchange(adjustedTotal, credit));
  }

  return Math.min(clamped, getMaxUniformDiscountAmountForExchange(products, credit));
};

/** Whether summary ledger: catalog + markup - discount === final. */
export const isExchangeSummaryCoherent = (
  catalogTotal: number,
  markupAmount: number,
  discountAmount: number,
  finalTotal: number,
): boolean => roundMoney(catalogTotal + markupAmount - discountAmount) === roundMoney(finalTotal);

/** Combined invalid state for exchange summary (credit rule + ledger coherence). */
export const isExchangeSummaryInvalid = (
  newItemsTotal: number,
  paidForExchangedItems: number,
  isSummaryCoherent: boolean,
): boolean => violatesExchangeCredit(newItemsTotal, paidForExchangedItems) || !isSummaryCoherent;

/** Total discount applied on new items (adjusted vs final). */
export const getExchangeAppliedDiscount = (adjustedTotal: number, finalTotal: number): number =>
  roundMoney(Math.max(0, adjustedTotal - finalTotal));

/** Amount still needed so final total meets exchange credit. */
export const getExchangeCreditGap = (finalTotal: number, credit: number): number => {
  const floor = getExchangeCredit(credit);
  if (finalTotal >= floor) return 0;
  return roundMoney(floor - finalTotal);
};

export const getExchangeCreditDetailMessage = (
  newItemsTotal: number,
  paidForExchangedItems: number,
  formatCurrency: (amount: number) => string,
): string =>
  `El total de piezas nuevas (${formatCurrency(newItemsTotal)}) no puede ser menor al crédito del intercambio (${formatCurrency(getExchangeCredit(paidForExchangedItems))}).`;

/** Alert when discounts exceed exchange credit (UI: Spanish). */
export const getExchangeCreditViolationMessage = (
  credit: number,
  formatCurrency: (amount: number) => string,
): string =>
  `El descuento supera el límite del intercambio. El total de piezas nuevas no puede quedar por debajo del crédito del intercambio (${formatCurrency(getExchangeCredit(credit))}). Ajusta los descuentos.`;

/** Step 1 reminder when catalog total is below exchange credit (UI: Spanish). */
export const getExchangeCreditStep1ReminderMessage = (
  credit: number,
  formatCurrency: (amount: number) => string,
): string =>
  `El total de piezas nuevas debe ser mayor o igual que ${formatCurrency(getExchangeCredit(credit))} (crédito del intercambio).`;
